/**
 * Labstep
 *
 * @module components/Log/Breadcrumb
 * @desc Breadcrumb for Log
 */

import {
  ICONS,
  type IEntityIconsProps,
} from 'labstep-web/constants/icons';
import { Breadcrumb } from 'labstep-web/core/Breadcrumb';
import type { IBreadcrumbLink } from 'labstep-web/core/Breadcrumb/types';
import { Group } from 'labstep-web/models/group.model';
import { Metadata } from 'labstep-web/models/metadata';
import { PartialEntity } from 'labstep-web/models/partial-entity.model';
import { routing } from 'labstep-web/services/navigation';
import { cloneDeep } from 'lodash';
import React from 'react';
import type { LogBreadcrumbProps } from './types';

export const LogBreadcrumb: React.FC<LogBreadcrumbProps> = ({
  log,
  entityName,
  showGroup = false,
}) => {
  const memoizedLinks = React.useMemo(() => {
    const links: IBreadcrumbLink[] = [];

    if (log.loggable_parents) {
      const loggableParents = cloneDeep(log.loggable_parents).map(
        (parent) => new PartialEntity(parent),
      );

      for (const loggableEntity of loggableParents.reverse()) {
        if (loggableEntity.entityClassSnakeCase === entityName) {
          continue;
        }

        if (
          showGroup === false &&
          loggableEntity.entityClassSnakeCase === Group.entityName
        ) {
          continue;
        }

        const link = {
          icon: ICONS[
            loggableEntity.entityClassSnakeCase as keyof typeof ICONS
          ]?.primary,
          name: loggableEntity.display_name,
          // FIXME route: log.entityLevelFive.route,
        } as any;

        const route = `${loggableEntity.entityClassSnakeCase}_show`;

        if (route in routing) {
          link.route = {
            to: route,
            params: {
              id: loggableEntity.id,
            },
          };
        }

        if (loggableEntity.is_template) {
          link.icon = (
            ICONS[
              loggableEntity.entityClassSnakeCase as keyof typeof ICONS
            ] as IEntityIconsProps
          )?.template;
        }

        if (loggableEntity.is_root) {
          continue;
        }

        const parentClasses = loggableParents.map(
          (p) => p.entityClassSnakeCase,
        );

        if (
          loggableEntity.entityClassSnakeCase ===
            Metadata.entityName &&
          !parentClasses.includes('experiment') &&
          !parentClasses.includes('protocol')
        ) {
          link.icon = ICONS[Metadata.entityName].secondary;
        }

        links.push(link);
      }
    }

    return links.reverse();
  }, [log.loggable_parents, entityName]);

  return <Breadcrumb links={memoizedLinks} />;
};
